/**
 * ConsistencyCheckDialog — compares this meeting to prior finalized
 * meetings in the same project. Purely informational: shows
 * terminology drift, contradictions, and abandoned commitments. No
 * auto-apply — the user reads findings and decides what to do
 * manually.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';

interface TerminologyDriftItem {
  currentTerm: string;
  priorTerm: string;
  priorMeetingId: string;
  rationale: string;
}
interface ContradictionItem {
  currentStatement: string;
  priorStatement: string;
  priorMeetingId: string;
  rationale: string;
}
interface AbandonedCommitment {
  description: string;
  priorMeetingId: string;
  priorMeetingDate: string;
  assignee?: string;
}
interface PriorMeetingSummary {
  id: string;
  title: string;
  meetingDate: string;
}
interface ConsistencyResponse {
  terminologyDrift: TerminologyDriftItem[];
  contradictions: ContradictionItem[];
  abandonedCommitments: AbandonedCommitment[];
  priorsUsed?: PriorMeetingSummary[];
  reason?: 'no_priors';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
}

export function ConsistencyCheckDialog({ isOpen, onClose, meetingId }: Props) {
  const mutation = useMutation({
    mutationFn: () =>
      api.post<ConsistencyResponse>(
        `/meetings/${meetingId}/consistency-check`,
      ),
  });

  useEffect(() => {
    if (isOpen && !mutation.data && !mutation.isPending && !mutation.isError) {
      mutation.mutate();
    }
    if (!isOpen) mutation.reset();
  }, [isOpen]);

  const data = mutation.data;
  const hasFindings =
    !!data &&
    (data.terminologyDrift.length > 0 ||
      data.contradictions.length > 0 ||
      data.abandonedCommitments.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Consistency check"
    >
      <ModalHeader
        title="Consistency check"
        subtitle="Claude compares this meeting to the project's prior finalized meetings and flags terminology drift, contradictions, and abandoned commitments. Informational - nothing is auto-applied."
        onClose={onClose}
      />
      <ModalBody>
        {mutation.isPending && (
          <div className="py-10 text-center text-sm italic text-charcoal/60">
            Reading prior meetings and comparing…
          </div>
        )}
        {mutation.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'Consistency check failed'}
          </div>
        )}
        {data?.reason === 'no_priors' && (
          <div className="rounded-2xl border border-dashed border-charcoal/20 p-6 text-center text-sm italic text-charcoal/60">
            No prior finalized meetings in this project yet - nothing
            to compare against.
          </div>
        )}
        {data && data.reason !== 'no_priors' && !hasFindings && (
          <div className="rounded-2xl border border-pine/25 bg-pine/5 p-4 text-sm text-pine">
            ✓ No inconsistencies found. This meeting reads consistently
            with the {data.priorsUsed?.length ?? 0} prior meeting
            {(data.priorsUsed?.length ?? 0) === 1 ? '' : 's'} Claude
            compared against.
          </div>
        )}
        {data && hasFindings && (
          <div className="space-y-6">
            {data.terminologyDrift.length > 0 && (
              <Section title="Terminology drift" count={data.terminologyDrift.length}>
                <ul className="space-y-2">
                  {data.terminologyDrift.map((d, i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-amber-200 bg-amber-50/40 p-3 text-[13px]"
                    >
                      <div className="text-charcoal">
                        This meeting uses{' '}
                        <span className="font-semibold text-charcoal">
                          &ldquo;{d.currentTerm}&rdquo;
                        </span>{' '}
                        where prior meetings used{' '}
                        <span className="font-semibold text-charcoal">
                          &ldquo;{d.priorTerm}&rdquo;
                        </span>
                        .
                      </div>
                      <div className="mt-1 text-[11px] text-charcoal/65">
                        {d.rationale}
                      </div>
                      <MeetingCitation id={d.priorMeetingId} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {data.contradictions.length > 0 && (
              <Section title="Contradictions" count={data.contradictions.length}>
                <ul className="space-y-2">
                  {data.contradictions.map((c, i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-red-200 bg-red-50/40 p-3 text-[13px]"
                    >
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                          This meeting
                        </div>
                        <div className="mt-0.5 text-charcoal">
                          {c.currentStatement}
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal/60">
                          Prior meeting
                        </div>
                        <div className="mt-0.5 text-charcoal/80">
                          {c.priorStatement}
                        </div>
                      </div>
                      <div className="mt-2 text-[11px] text-charcoal/65">
                        {c.rationale}
                      </div>
                      <MeetingCitation id={c.priorMeetingId} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {data.abandonedCommitments.length > 0 && (
              <Section
                title="Abandoned commitments"
                count={data.abandonedCommitments.length}
              >
                <ul className="space-y-2">
                  {data.abandonedCommitments.map((a, i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-charcoal/15 bg-cloud/40 p-3 text-[13px]"
                    >
                      <div className="text-charcoal">{a.description}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-charcoal/60">
                        {a.assignee && (
                          <span className="rounded-full bg-pine/10 px-2 py-0.5 text-pine">
                            {a.assignee}
                          </span>
                        )}
                        <span>From {a.priorMeetingDate}</span>
                      </div>
                      <MeetingCitation id={a.priorMeetingId} />
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/55">
          {data?.priorsUsed
            ? `Compared against ${data.priorsUsed.length} prior meeting${data.priorsUsed.length === 1 ? '' : 's'}`
            : ''}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90"
        >
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-pine">
        {title}
        <span className="rounded-full bg-pine/10 px-2 py-0.5 text-[10px]">
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function MeetingCitation({ id }: { id: string }) {
  return (
    <div className="mt-1.5">
      <Link
        to={`/meetings/${id}/edit`}
        className="text-[11px] text-pine underline-offset-2 hover:underline"
      >
        View prior meeting →
      </Link>
    </div>
  );
}
